import React, { useState } from 'react';
import { Container, Row, Col, Card, Badge, Button, Form, Table, Alert, Spinner, Modal } from 'react-bootstrap';
import {
    FileText,
    Download,
    Calendar,
    Users,
    Monitor,
    TrendingUp,
    BarChart3,
    PieChart,
    Filter,
    Eye,
    RefreshCw,
    CheckCircle,
    Clock,
    AlertTriangle,
    Award,
    MapPin,
    DollarSign,
    Shield,
    Laptop
} from 'lucide-react';

const ExecutiveReports = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [selectedReport, setSelectedReport] = useState('overview');
    const [dateRange, setDateRange] = useState('last-30-days');
    const [reportData, setReportData] = useState(null);
    const [showPreview, setShowPreview] = useState(false);
    const [exportFormat, setExportFormat] = useState('pdf');
    const [customDateRange, setCustomDateRange] = useState({
        startDate: '',
        endDate: ''
    });

    // User role - in real app this would come from your auth context
    const userRole = 'MANAGEMENT'; // Replace with actual auth context

    // Base URL - replace with your actual API base URL
    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

    // Helper function to get auth token
    const getAuthToken = () => {
        return localStorage.getItem('accessToken');
    };

    // Available report types
    const reportTypes = [
        {
            id: 'overview',
            title: 'Executive Overview',
            description: 'Comprehensive organizational summary with key metrics and trends',
            icon: BarChart3,
            color: 'primary',
            available: ['MANAGEMENT', 'ICT', 'HR'],
            endpoint: '/api/management/dashboard-overview'
        },
        {
            id: 'staff',
            title: 'Staff Analytics Report',
            description: 'Detailed staff demographics, performance, and utilization analysis',
            icon: Users,
            color: 'success',
            available: ['MANAGEMENT', 'HR'],
            endpoint: '/api/management/staff-analytics'
        },
        {
            id: 'assets',
            title: 'Asset Management Report',
            description: 'Complete asset inventory, utilization, and financial analysis',
            icon: Monitor,
            color: 'info',
            available: ['MANAGEMENT', 'ICT'],
            endpoint: '/api/management/asset-analytics'
        },
        {
            id: 'operational',
            title: 'Operational Efficiency',
            description: 'KPIs, process efficiency, and operational performance metrics',
            icon: TrendingUp,
            color: 'warning',
            available: ['MANAGEMENT'],
            endpoint: '/api/management/operational-kpis'
        },
        {
            id: 'compliance',
            title: 'Compliance & Training',
            description: 'Training completion rates, compliance status, and certification tracking',
            icon: Award,
            color: 'success',
            available: ['MANAGEMENT', 'HR'],
            endpoint: '/api/management/training-analytics'
        },
        {
            id: 'checkout',
            title: 'Checkout Analytics',
            description: 'Checkout process efficiency and departmental performance',
            icon: CheckCircle,
            color: 'info',
            available: ['MANAGEMENT', 'ICT', 'HR', 'SECURITY'],
            endpoint: '/api/management/checkout-analytics'
        }
    ];

    // Date range options
    const dateRangeOptions = [
        { value: 'last-7-days', label: 'Last 7 Days' },
        { value: 'last-30-days', label: 'Last 30 Days' },
        { value: 'last-90-days', label: 'Last 90 Days' },
        { value: 'last-6-months', label: 'Last 6 Months' },
        { value: 'last-year', label: 'Last Year' },
        { value: 'ytd', label: 'Year to Date' },
        { value: 'custom', label: 'Custom Range' }
    ];

    // Export format options
    const exportFormats = [
        { value: 'pdf', label: 'PDF Document', icon: FileText },
        { value: 'excel', label: 'Excel Spreadsheet', icon: FileText },
        { value: 'csv', label: 'CSV Data', icon: FileText },
        { value: 'json', label: 'JSON Data', icon: FileText }
    ];

    // API call to fetch data from your backend
    const fetchReportData = async (reportType) => {
        const token = getAuthToken();
        if (!token) {
            throw new Error('Authentication token is missing. Please log in again.');
        }

        const report = reportTypes.find(r => r.id === reportType);
        if (!report) {
            throw new Error('Invalid report type selected.');
        }

        try {
            const response = await fetch(`${BASE_URL}${report.endpoint}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                mode: 'cors',
                credentials: 'omit'
            });

            if (!response.ok) {
                let errorMessage;

                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}`;
                } catch {
                    errorMessage = await response.text() || `HTTP ${response.status}: ${response.statusText}`;
                }

                if (response.status === 401) {
                    throw new Error('Your session has expired. Please log in again.');
                }
                if (response.status === 403) {
                    throw new Error('You do not have permission to access this report.');
                }
                if (response.status === 404) {
                    throw new Error(`Report endpoint not found: ${report.endpoint}`);
                }
                if (response.status === 500) {
                    throw new Error('Server error occurred. Please check backend logs.');
                }

                throw new Error(errorMessage);
            }

            const data = await response.json();
            return data;

        } catch (error) {
            if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                throw new Error(`Cannot connect to API server. Please check your connection and try again.`);
            }

            throw error;
        }
    };

    // Generate report data
    const generateReport = async () => {
        setLoading(true);
        setError(null);

        try {
            const data = await fetchReportData(selectedReport);

            // Structure the data for the report
            const structuredData = {
                report_type: selectedReport,
                generated_at: new Date().toISOString(),
                data: data,
                metadata: {
                    date_range: dateRange,
                    custom_dates: dateRange === 'custom' ? customDateRange : null
                }
            };

            setReportData(structuredData);
            setShowPreview(true);

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Export report using your existing endpoint
    const exportReport = async (format) => {
        setLoading(true);
        try {
            const token = getAuthToken();
            let dateParams = {};

            if (dateRange === 'custom') {
                dateParams = {
                    start_date: customDateRange.startDate,
                    end_date: customDateRange.endDate
                };
            } else {
                dateParams = { period: dateRange };
            }

            const queryParams = new URLSearchParams({
                report_type: selectedReport,
                format: format,
                ...dateParams
            }).toString();

            const response = await fetch(`${BASE_URL}/api/management/export-report?${queryParams}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Export failed: ${response.status}`);
            }

            if (format === 'json') {
                // For JSON, download as file
                const data = await response.json();
                const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${selectedReport}-report-${new Date().toISOString().split('T')[0]}.json`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            } else {
                // For other formats, handle as blob
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${selectedReport}-report-${new Date().toISOString().split('T')[0]}.${format}`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
            }

        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Get available reports for current user role
    const getAvailableReports = () => {
        return reportTypes.filter(report =>
            report.available.includes(userRole)
        );
    };

    // Render report preview based on actual data structure
    const renderReportPreview = () => {
        if (!reportData || !reportData.data) return null;

        const data = reportData.data;

        switch (selectedReport) {
            case 'overview':
                return (
                    <div className="space-y-4">
                        <Row className="g-3 mb-4">
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <Users size={32} className="text-primary mb-2" />
                                        <h4>{data.staff_metrics?.total_staff || 0}</h4>
                                        <small className="text-muted">Total Staff</small>
                                        <div className="mt-1">
                                            <small className="text-success">
                                                {data.staff_metrics?.active_staff || 0} active
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <Monitor size={32} className="text-success mb-2" />
                                        <h4>{data.asset_metrics?.total_assets || 0}</h4>
                                        <small className="text-muted">Total Assets</small>
                                        <div className="mt-1">
                                            <small className="text-info">
                                                {data.asset_metrics?.utilization_rate || 0}% utilized
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <CheckCircle size={32} className="text-info mb-2" />
                                        <h4>{data.checkout_metrics?.completion_rate || 0}%</h4>
                                        <small className="text-muted">Checkout Efficiency</small>
                                        <div className="mt-1">
                                            <small className="text-warning">
                                                {data.checkout_metrics?.pending_checkouts || 0} pending
                                            </small>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                            <Col md={3}>
                                <Card className="text-center">
                                    <Card.Body>
                                        <Award size={32} className="text-warning mb-2" />
                                        <h4>{data.training_metrics?.total_enrollments || 0}</h4>
                                        <small className="text-muted">Training Enrollments</small>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>

                        {data.staff_metrics?.by_role && Object.keys(data.staff_metrics.by_role).length > 0 && (
                            <Card className="mb-4">
                                <Card.Header>
                                    <h6 className="mb-0">Staff Distribution by Role</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Table responsive>
                                        <thead>
                                        <tr>
                                            <th>Role</th>
                                            <th>Count</th>
                                            <th>Percentage</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {Object.entries(data.staff_metrics.by_role).map(([role, count]) => (
                                            <tr key={role}>
                                                <td>
                                                    <Badge bg="secondary" className="me-2">
                                                        {role}
                                                    </Badge>
                                                </td>
                                                <td>{count}</td>
                                                <td>
                                                    {((count / data.staff_metrics.total_staff) * 100).toFixed(1)}%
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        )}

                        {data.asset_metrics?.by_status && (
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">Asset Status Overview</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-3">
                                        {Object.entries(data.asset_metrics.by_status).map(([status, count]) => (
                                            <Col md={3} key={status}>
                                                <div className="text-center p-3 bg-light rounded">
                                                    <h5 className="mb-1">{count}</h5>
                                                    <small className="text-muted">{status}</small>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}
                    </div>
                );

            case 'staff':
                return (
                    <div className="space-y-4">
                        {data.demographics && (
                            <Card className="mb-4">
                                <Card.Header>
                                    <h6 className="mb-0">Staff Demographics</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-4">
                                        {data.demographics.by_gender && (
                                            <Col md={4}>
                                                <h6>By Gender</h6>
                                                {Object.entries(data.demographics.by_gender).map(([gender, count]) => (
                                                    <div key={gender} className="d-flex justify-content-between mb-2">
                                                        <span>{gender}</span>
                                                        <Badge bg="primary">{count}</Badge>
                                                    </div>
                                                ))}
                                            </Col>
                                        )}
                                        {data.demographics.by_nationality && (
                                            <Col md={4}>
                                                <h6>By Nationality</h6>
                                                {Object.entries(data.demographics.by_nationality).slice(0, 5).map(([nationality, count]) => (
                                                    <div key={nationality} className="d-flex justify-content-between mb-2">
                                                        <span>{nationality}</span>
                                                        <Badge bg="info">{count}</Badge>
                                                    </div>
                                                ))}
                                            </Col>
                                        )}
                                        {data.demographics.by_age_group && (
                                            <Col md={4}>
                                                <h6>By Age Group</h6>
                                                {Object.entries(data.demographics.by_age_group).map(([age, count]) => (
                                                    <div key={age} className="d-flex justify-content-between mb-2">
                                                        <span>{age}</span>
                                                        <Badge bg="success">{count}</Badge>
                                                    </div>
                                                ))}
                                            </Col>
                                        )}
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}

                        {data.employment && (
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">Employment Analysis</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-4">
                                        {data.employment.by_contract_type && (
                                            <Col md={6}>
                                                <h6>Contract Types</h6>
                                                <Table responsive size="sm">
                                                    <tbody>
                                                    {Object.entries(data.employment.by_contract_type).map(([type, count]) => (
                                                        <tr key={type}>
                                                            <td>{type}</td>
                                                            <td><Badge bg="warning">{count}</Badge></td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </Table>
                                            </Col>
                                        )}
                                        {data.employment.dependants_analysis && (
                                            <Col md={6}>
                                                <h6>Dependants Analysis</h6>
                                                <Table responsive size="sm">
                                                    <tbody>
                                                    {Object.entries(data.employment.dependants_analysis).map(([category, count]) => (
                                                        <tr key={category}>
                                                            <td>{category}</td>
                                                            <td><Badge bg="secondary">{count}</Badge></td>
                                                        </tr>
                                                    ))}
                                                    </tbody>
                                                </Table>
                                            </Col>
                                        )}
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}
                    </div>
                );

            case 'assets':
                return (
                    <div className="space-y-4">
                        {data.financial && (
                            <Row className="g-3 mb-4">
                                <Col md={4}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <DollarSign size={32} className="text-success mb-2" />
                                            <h4>${(data.financial.total_value || 0).toLocaleString()}</h4>
                                            <small className="text-muted">Total Asset Value</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <TrendingUp size={32} className="text-primary mb-2" />
                                            <h4>{data.utilization?.assignment_rate || 0}%</h4>
                                            <small className="text-muted">Assignment Rate</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={4}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <AlertTriangle size={32} className="text-warning mb-2" />
                                            <h4>{data.utilization?.maintenance || 0}</h4>
                                            <small className="text-muted">In Maintenance</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        )}

                        {data.financial?.value_by_type && (
                            <Card className="mb-4">
                                <Card.Header>
                                    <h6 className="mb-0">Asset Value by Type</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Table responsive>
                                        <thead>
                                        <tr>
                                            <th>Asset Type</th>
                                            <th>Count</th>
                                            <th>Total Value</th>
                                            <th>Average Value</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {data.financial.value_by_type.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.type}</td>
                                                <td><Badge bg="primary">{item.count}</Badge></td>
                                                <td>${item.total_value.toLocaleString()}</td>
                                                <td>${item.average_value.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        )}

                        {data.distribution?.by_location && (
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">Asset Distribution by Location</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row className="g-3">
                                        {Object.entries(data.distribution.by_location).map(([location, count]) => (
                                            <Col md={3} key={location}>
                                                <div className="text-center p-3 bg-light rounded">
                                                    <MapPin size={24} className="text-info mb-2" />
                                                    <h6 className="mb-1">{count}</h6>
                                                    <small className="text-muted">{location}</small>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}
                    </div>
                );

            case 'compliance':
                return (
                    <div className="space-y-4">
                        {data.completion_rates && (
                            <Card className="mb-4">
                                <Card.Header>
                                    <h6 className="mb-0">Training Completion Rates</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Table responsive>
                                        <thead>
                                        <tr>
                                            <th>Training Program</th>
                                            <th>Total Enrolled</th>
                                            <th>Completed</th>
                                            <th>Completion Rate</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {Object.entries(data.completion_rates).map(([program, stats]) => (
                                            <tr key={program}>
                                                <td>{program}</td>
                                                <td><Badge bg="info">{stats.total}</Badge></td>
                                                <td><Badge bg="success">{stats.completed}</Badge></td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <span className="me-2">{stats.completion_rate}%</span>
                                                        <div className="progress flex-grow-1" style={{height: '6px'}}>
                                                            <div
                                                                className="progress-bar"
                                                                style={{width: `${stats.completion_rate}%`}}
                                                            ></div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        )}

                        {data.summary && (
                            <Row className="g-3">
                                <Col md={3}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <Award size={32} className="text-primary mb-2" />
                                            <h4>{data.summary.total_programs}</h4>
                                            <small className="text-muted">Total Programs</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <Users size={32} className="text-success mb-2" />
                                            <h4>{data.summary.total_enrollments}</h4>
                                            <small className="text-muted">Total Enrollments</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <CheckCircle size={32} className="text-info mb-2" />
                                            <h4>{data.summary.overall_completion_rate}%</h4>
                                            <small className="text-muted">Overall Completion</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <Calendar size={32} className="text-warning mb-2" />
                                            <h4>{Object.keys(data.by_year || {}).length}</h4>
                                            <small className="text-muted">Active Years</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        )}
                    </div>
                );

            case 'operational':
                return (
                    <div className="space-y-4">
                        {data.kpis && (
                            <Row className="g-3 mb-4">
                                <Col md={3}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <Users size={32} className="text-primary mb-2" />
                                            <h4>{data.kpis.staff_retention_rate}%</h4>
                                            <small className="text-muted">Staff Retention</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <Monitor size={32} className="text-success mb-2" />
                                            <h4>{data.kpis.asset_utilization_rate}%</h4>
                                            <small className="text-muted">Asset Utilization</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <CheckCircle size={32} className="text-info mb-2" />
                                            <h4>{data.kpis.checkout_completion_rate}%</h4>
                                            <small className="text-muted">Checkout Completion</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={3}>
                                    <Card className="text-center">
                                        <Card.Body>
                                            <Award size={32} className="text-warning mb-2" />
                                            <h4>{data.kpis.training_completion_rate}%</h4>
                                            <small className="text-muted">Training Completion</small>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                        )}

                        {data.financial_metrics && (
                            <Card className="mb-4">
                                <Card.Body>
                                    <Row className="g-3">
                                        <Col md={4}>
                                            <div className="text-center p-3 bg-light rounded">
                                                <DollarSign size={24} className="text-success mb-2" />
                                                <h5>${data.financial_metrics.total_asset_value.toLocaleString()}</h5>
                                                <small className="text-muted">Total Asset Value</small>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="text-center p-3 bg-light rounded">
                                                <TrendingUp size={24} className="text-primary mb-2" />
                                                <h5>${data.financial_metrics.average_asset_value}</h5>
                                                <small className="text-muted">Average Asset Value</small>
                                            </div>
                                        </Col>
                                        <Col md={4}>
                                            <div className="text-center p-3 bg-light rounded">
                                                <Users size={24} className="text-info mb-2" />
                                                <h5>${data.financial_metrics.cost_per_employee}</h5>
                                                <small className="text-muted">Cost per Employee</small>
                                            </div>
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        )}

                        {data.operational_metrics && (
                            <Card>
                                <Card.Header>
                                    <h6 className="mb-0">Operational Statistics</h6>
                                </Card.Header>
                                <Card.Body>
                                    <Table responsive>
                                        <tbody>
                                        <tr>
                                            <td><strong>Total Staff</strong></td>
                                            <td><Badge bg="primary">{data.operational_metrics.total_staff}</Badge></td>
                                        </tr>
                                        <tr>
                                            <td><strong>Active Staff</strong></td>
                                            <td><Badge bg="success">{data.operational_metrics.active_staff}</Badge></td>
                                        </tr>
                                        </tbody>
                                    </Table>
                                </Card.Body>
                            </Card>
                        )}
                    </div>
                );

            default:
                return null;
        }
    };

    // Handle report type change
    const handleReportChange = (reportId) => {
        setSelectedReport(reportId);
        setReportData(null);
        setShowPreview(false);
    };

    // Handle date range change
    const handleDateRangeChange = (e) => {
        setDateRange(e.target.value);
        setReportData(null);
        setShowPreview(false);
    };

    // Handle custom date change
    const handleCustomDateChange = (e) => {
        setCustomDateRange({
            ...customDateRange,
            [e.target.name]: e.target.value
        });
    };

    // Handle export format change
    const handleExportFormatChange = (e) => {
        setExportFormat(e.target.value);
    };

    // Handle generate report click
    const handleGenerateReport = () => {
        if (dateRange === 'custom' && (!customDateRange.startDate || !customDateRange.endDate)) {
            setError('Please select both start and end dates for custom range');
            return;
        }
        generateReport();
    };

    // Reset filters
    const handleResetFilters = () => {
        setDateRange('last-30-days');
        setCustomDateRange({ startDate: '', endDate: '' });
        setSelectedReport('overview');
        setReportData(null);
        setShowPreview(false);
        setError(null);
    };

    return (
        <Container fluid className="py-4">
            <h2 className="mb-4">Executive Reports Dashboard</h2>

            {/* Error Alert */}
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    <AlertTriangle size={20} className="me-2" />
                    {error}
                </Alert>
            )}

            {/* Filters Section */}
            <Card className="mb-4">
                <Card.Header>
                    <h5 className="mb-0 d-flex align-items-center">
                        <Filter size={20} className="me-2" />
                        Report Filters
                    </h5>
                </Card.Header>
                <Card.Body>
                    <Row className="g-3">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Select Report Type</Form.Label>
                                <Form.Select
                                    value={selectedReport}
                                    onChange={(e) => handleReportChange(e.target.value)}
                                >
                                    {getAvailableReports().map(report => (
                                        <option key={report.id} value={report.id}>
                                            {report.title}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Date Range</Form.Label>
                                <Form.Select
                                    value={dateRange}
                                    onChange={handleDateRangeChange}
                                >
                                    {dateRangeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Export Format</Form.Label>
                                <Form.Select
                                    value={exportFormat}
                                    onChange={handleExportFormatChange}
                                >
                                    {exportFormats.map(format => (
                                        <option key={format.value} value={format.value}>
                                            {format.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                    {dateRange === 'custom' && (
                        <Row className="g-3 mt-2">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Start Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="startDate"
                                        value={customDateRange.startDate}
                                        onChange={handleCustomDateChange}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>End Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        name="endDate"
                                        value={customDateRange.endDate}
                                        onChange={handleCustomDateChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    <div className="mt-3 d-flex gap-2">
                        <Button
                            variant="primary"
                            onClick={handleGenerateReport}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Eye size={16} className="me-2" />
                                    Generate Report
                                </>
                            )}
                        </Button>
                        <Button
                            variant="outline-secondary"
                            onClick={handleResetFilters}
                            disabled={loading}
                        >
                            <RefreshCw size={16} className="me-2" />
                            Reset Filters
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            {/* Report Preview Section */}
            {showPreview && reportData && (
                <Card className="mb-4">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            {reportTypes.find(r => r.id === selectedReport)?.title} Preview
                        </h5>
                        <Button
                            variant="success"
                            onClick={() => exportReport(exportFormat)}
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <Download size={16} className="me-2" />
                                    Export as {exportFormat.toUpperCase()}
                                </>
                            )}
                        </Button>
                    </Card.Header>
                    <Card.Body>
                        {renderReportPreview()}
                    </Card.Body>
                </Card>
            )}

            {/* Modal for Report Preview */}
            <Modal
                show={showPreview}
                onHide={() => setShowPreview(false)}
                size="xl"
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {reportTypes.find(r => r.id === selectedReport)?.title} Preview
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {renderReportPreview()}
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowPreview(false)}
                    >
                        Close
                    </Button>
                    <Button
                        variant="success"
                        onClick={() => exportReport(exportFormat)}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <Spinner size="sm" className="me-2" />
                                Exporting...
                            </>
                        ) : (
                            <>
                                <Download size={16} className="me-2" />
                                Export as {exportFormat.toUpperCase()}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default ExecutiveReports;